import { useMutation, useQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import type { AxiosError } from 'axios'
import { useState, type FormEvent } from 'react'

import { AUTH } from '@brickup/shared/locales/de/auth'
import { COMMON } from '@brickup/shared/locales/de/common'
import { resetPassword, validateResetToken } from '@/api/auth'
import { TextInput } from '@/components/base'
import {
  PublicPageActions,
  PublicPageBackButton,
  PublicPageCard,
  PublicPageCardLoading,
  PublicPageCardMessage,
  PublicPageHeading,
} from '@/components/Public/PublicPageCard'
import { SnackbarType } from '@/components/Snackbar'
import { MIN_PASSWORD_LENGTH } from '@/constants/password'
import { useMutationErrorSnackbar } from '@/hooks/use-mutation-error-snackbar'
import { useSnackbar } from '@/hooks/use-snackbar'

export const Route = createFileRoute('/password-reset/$token')({
  component: PasswordResetScreen,
})

function PasswordResetScreen() {
  const { token } = Route.useParams()
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [success, setSuccess] = useState(false)
  const addSnackbar = useSnackbar()
  const showError = useMutationErrorSnackbar()

  const {
    data: tokenData,
    isLoading: isValidating,
    error: validationError,
  } = useQuery({
    queryKey: ['reset-token', token],
    queryFn: () => validateResetToken(token),
    retry: false,
  })

  const { mutate, isPending } = useMutation({
    mutationFn: () => resetPassword(token, newPassword),
    onSuccess: () => {
      setSuccess(true)
      addSnackbar({
        title: AUTH.passwordResetSuccess,
        type: SnackbarType.SUCCESS,
      })
    },
    onError: (error: AxiosError) =>
      showError(error, COMMON.errors.networkError),
  })

  const onSubmit = (event: FormEvent) => {
    event.preventDefault()

    if (newPassword.length < MIN_PASSWORD_LENGTH) {
      addSnackbar({
        title: AUTH.passwordTooShort(MIN_PASSWORD_LENGTH),
        type: SnackbarType.ERROR,
      })
      return
    }

    if (newPassword !== confirmPassword) {
      addSnackbar({
        title: AUTH.passwordsDontMatch,
        type: SnackbarType.ERROR,
      })
      return
    }

    mutate()
  }

  if (isValidating) {
    return <PublicPageCardLoading text={AUTH.validatingToken} />
  }

  if (validationError || !tokenData?.valid) {
    return (
      <PublicPageCardMessage
        title={AUTH.invalidResetToken}
        description={AUTH.invalidResetTokenDescription}
        variant="error"
      >
        <PublicPageActions label={AUTH.requestNewLink} to="/forgot-password" />
      </PublicPageCardMessage>
    )
  }

  if (success) {
    return (
      <PublicPageCardMessage
        title={AUTH.passwordResetSuccess}
        description={AUTH.passwordResetSuccessDescription}
        variant="success"
      >
        <PublicPageBackButton />
      </PublicPageCardMessage>
    )
  }

  return (
    <PublicPageCard>
      <PublicPageHeading
        title={AUTH.resetPassword}
        description={
          tokenData.email
            ? `${AUTH.resetPasswordDescription} ${tokenData.email}`
            : AUTH.resetPasswordDescription
        }
      />

      <form onSubmit={onSubmit} className="flex flex-col gap-3">
        <TextInput
          type="password"
          autoComplete="new-password"
          placeholder={COMMON.newPassword}
          value={newPassword}
          disabled={isPending}
          onValueChange={value => setNewPassword(value ?? '')}
        />

        <TextInput
          type="password"
          autoComplete="new-password"
          placeholder={AUTH.confirmPassword}
          value={confirmPassword}
          disabled={isPending}
          onValueChange={value => setConfirmPassword(value ?? '')}
        />

        <PublicPageActions label={AUTH.resetPassword} loading={isPending} />
      </form>
    </PublicPageCard>
  )
}
